import { useEffect, useState } from "react";
import { messengerService } from "../../../functions/messenger";
import { getUrlDomain, normalizeUrl } from "../../../helpers/linkify";

/**
 * Rich link preview card for chat messages.
 * Fetches Open Graph / meta tags via the messenger link-preview API.
 */
export default function LinkPreview({ url, token, isOwn = false }) {
  const [preview, setPreview] = useState(null);
  const [failed, setFailed] = useState(false);
  const href = normalizeUrl(url);
  const domain = getUrlDomain(href);

  useEffect(() => {
    let cancelled = false;
    if (!href || !token) return undefined;

    setFailed(false);
    setPreview(null);

    messengerService
      .getLinkPreview(href, token)
      .then((data) => {
        if (cancelled) return;
        if (data && (data.title || data.description || data.image)) {
          setPreview(data);
        } else {
          setFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [href, token]);

  if (failed || !preview) return null;

  const title = preview.title || domain;
  const description = preview.description || "";
  const image = preview.image || "";
  const displayDomain = preview.domain || domain;

  const openLink = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const copyLink = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(href);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className={`message_link_preview ${isOwn ? "own" : "other"}`}>
      <a
        href={href}
        className="message_link_preview_card"
        target="_blank"
        rel="noopener noreferrer"
        onClick={openLink}
      >
        {image ? (
          <div className="message_link_preview_image_wrap">
            <img
              src={image}
              alt=""
              className="message_link_preview_image"
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        ) : null}
        <div className="message_link_preview_meta">
          {displayDomain ? (
            <span className="message_link_preview_domain">{displayDomain}</span>
          ) : null}
          {title ? (
            <span className="message_link_preview_title">{title}</span>
          ) : null}
          {description ? (
            <span className="message_link_preview_desc">{description}</span>
          ) : null}
        </div>
      </a>
      <button
        type="button"
        className="message_link_preview_copy"
        onClick={copyLink}
        title="Copy link"
        aria-label="Copy link"
      >
        Copy link
      </button>
    </div>
  );
}
